import React, { useState } from "react";
import { useAuth } from "utilities/auth";
import { isInternal, isClient } from "utilities/userType";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import RelatedRecordsCard from "components/Companies/RelatedRecordsCard";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { StatusBadge } from "components/Account/StatusBadge";
import { InfoField } from "components/Account/InfoField";
import Skeleton from "components/Skeleton";
import SourceBadge from "components/SourceBadge";
import ParentCompanyCard from "components/Companies/ParentCompanyCard";
import ChildCompaniesCard from "components/Companies/ChildCompaniesCard";
import SourcesCard from "components/Companies/SourcesCard";
import PerformanceCard from "components/Companies/PerformanceCard";
import {
  FEATURE_OPTIONS,
  COMPANY_STATUS_OPTIONS,
  tierOptionsFor,
} from "constants/companies";
import {
  childrenRetieredCount,
  childrenRetieredMessage,
} from "utilities/tierInheritance";
import { Building2, Edit3, Tag, Calendar, Shield, MessageSquare, Copy, Check } from "lucide-react";

// SOUND-296: the tier / feature / status vocabulary is defined once in
// constants/companies. It used to be declared here AND in the create modals
// AND in searchConfig, and the copies had drifted.
const STATUS_OPTIONS = COMPANY_STATUS_OPTIONS;

export default function OverviewTab({ company, loading, refetch }) {
  const auth = useAuth();
  const internal = isInternal(auth.user);
  const { toast } = useToast();
  const [showEdit, setShowEdit] = useState(false);
  const [copied, setCopied] = useState(false);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton.Rect className="h-48 w-full" />
        <Skeleton.Rect className="h-32 w-full" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="surface rounded-xl ring-1 ring-border p-8 text-center">
        <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Company not found.</p>
      </div>
    );
  }

  const formatDate = (timestamp) =>
    timestamp
      ? new Date(timestamp).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "—";

  return (
    // Matches the registration tab and the ticket detail page: a fixed 320px
    // rail, main column takes the remainder.
    <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-6">
        {/* Company Info */}
      <div className="surface">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-brand/20 rounded-lg">
              <Building2 className="w-5 h-5 text-brand" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Company Details</h2>
          </div>
          {internal && (
            <button className="btn btn-outline btn-sm" onClick={() => setShowEdit(true)}>
              <Edit3 className="w-4 h-4" />
              Edit
            </button>
          )}
        </div>
        <div className="p-6">
          {/* Source (SOUND-293) — hidden from clients */}
          {!isClient(auth.user) && company.source && (
            <div className="mb-4 pb-4 border-b border-border">
              <dt className="text-sm font-medium text-foreground mb-1">Source</dt>
              <dd>
                <SourceBadge source={company.source} />
              </dd>
            </div>
          )}
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
            <InfoField label="Company Name" value={company.name} />
            <InfoField label="Company Type" value={company.companyType} />
            <InfoField label="Tier" value={company.tier} />
            <div>
              <dt className="text-sm font-medium text-foreground mb-1">Status</dt>
              <dd><StatusBadge status={company.status} /></dd>
            </div>
            <InfoField label="Slug" value={company.slug} />
            <InfoField label="Company ID" value={company.companyId} />
          </dl>
        </div>
      </div>

      {/* 30-day performance (companies in the data pipeline) — SOUND-312 */}
      {company.features?.includes("data-pipeline") && <PerformanceCard company={company} />}

      {/* Parent Company (internal-only) */}
      {internal && company.companyType === "client" && (
        <ParentCompanyCard company={company} refetch={refetch} />
      )}

      {/* Child Companies (internal-only) — only for eligible parents (2-level depth rule) */}
      {internal && company.companyType === "client" && !company.parentCompanyId && (
        <ChildCompaniesCard company={company} refetch={refetch} />
      )}

      {/* Sources (SOUND-294, internal-only) — retroactive partner attribution */}
      {internal && <SourcesCard company={company} refetch={refetch} />}

      {/* Telegram Registration */}
      {internal && (
        <div className="surface">
          <div className="px-6 py-4 border-b border-border flex items-center space-x-3">
            <div className="p-2 bg-brand/20 rounded-lg">
              <MessageSquare className="w-5 h-5 text-brand" />
            </div>
            <h2 className="text-lg font-semibold text-foreground">Telegram Notifications</h2>
          </div>
          <div className="p-6">
            <p className="text-sm text-muted-foreground mb-3">
              Subscribe to notifications for this company in Telegram:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-muted/50 text-sm font-mono px-4 py-2.5 rounded-lg border border-border truncate">
                /register {company.companyId} {company.name}
              </code>
              <button
                className="btn btn-outline btn-sm shrink-0"
                onClick={() => {
                  navigator.clipboard.writeText(`/register ${company.companyId} ${company.name}`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Features */}
      <div className="surface">
        <div className="px-6 py-4 border-b border-border flex items-center space-x-3">
          <div className="p-2 bg-brand/20 rounded-lg">
            <Tag className="w-5 h-5 text-brand" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Features</h2>
        </div>
        <div className="p-6">
          {company.features?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {company.features.map((feature) => (
                <span key={feature} className="badge badge-brand">
                  {feature}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No features enabled.</p>
          )}
        </div>
      </div>

      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        {/* SOUND-643 Phase 3 — the record home leads with what the company HAS.
            Overview previously showed seven fields (name, type, tier, slug, id,
            created, updated) and nothing about its MIDs, applications, tickets
            or files. */}
        <RelatedRecordsCard companyId={company.companyId} />

        {/* Timeline — ticketing chrome, matching Related Records above it and
            the registration tab's rail. Two dates do not warrant a two-column
            grid inside a 320px rail. */}
        <section className="surface p-4">
          <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            Timeline
          </h3>
          <dl className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Created
              </dt>
              <dd className="text-xs text-foreground">{formatDate(company.createdAt)}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Last Updated
              </dt>
              <dd className="text-xs text-foreground">{formatDate(company.updatedAt)}</dd>
            </div>
          </dl>
        </section>

      </div>

      {/* Edit Modal */}
      {showEdit && (
        <CompanyEditModal
          company={company}
          onClose={() => setShowEdit(false)}
          onSaved={() => { setShowEdit(false); refetch(); }}
        />
      )}
    </div>
  );
}

function CompanyEditModal({ company, onClose, onSaved }) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: company.name || "",
    tier: company.tier || "",
    status: company.status || "pending",
    slug: company.slug || "",
    features: company.features || [],
  });

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFeatureToggle = (feature) => {
    setForm((prev) => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter((f) => f !== feature)
        : [...prev.features, feature],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      const res = await makeApiRequest(
        "put",
        apiRoutes.updateCompany(company.companyId),
        auth.user,
        effectiveCompanyId,
        payload
      );
      // SOUND-795: a parent moving TO Managed Services re-tiers its children
      // server-side; the response reports how many (missing field = 0).
      const retiered = childrenRetieredCount(res?.data);
      toast({
        status: "success",
        title: "Company updated",
        description: childrenRetieredMessage(retiered) || undefined,
        ...(retiered > 0 ? { duration: 6000 } : {}),
      });
      // onSaved → refetch(), which flips useCompany's `loading` and makes
      // CompanyDetails render its skeleton: this tab (and ChildCompaniesCard)
      // remounts and re-fetches the children, so their new tiers show.
      onSaved();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Failed to update company",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-lg shadow-lg">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Edit Company</h3>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Name</label>
            <input
              type="text"
              className="input input-bordered w-full"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Tier</label>
            <select
              className="input input-bordered w-full"
              value={form.tier}
              onChange={(e) => handleChange("tier", e.target.value)}
            >
              <option value="">Select tier...</option>
              {tierOptionsFor(company.companyType).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Status</label>
            <select
              className="input input-bordered w-full"
              value={form.status}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Slug</label>
            <input
              type="text"
              className="input input-bordered w-full"
              value={form.slug}
              onChange={(e) => handleChange("slug", e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Features</label>
            <div className="grid grid-cols-2 gap-2">
              {FEATURE_OPTIONS.map((feature) => (
                <label key={feature} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.features.includes(feature)}
                    onChange={() => handleFeatureToggle(feature)}
                    className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
                  />
                  {feature}
                </label>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button className="btn btn-ghost flex-1" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary flex-1" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
